import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useCreateClientMarketOrder, useGetAllClientMarketInstruments } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/dialog'
import { Form } from '@/components/form'
import { useToast } from '@/components/toast/useToast'

import CreateOrderClientRefForm, { clientRefSchema } from './formComponents/clientRefForm'
import CreateOrderInstrumentSymbolForm, { instrumentSymbolSchema } from './formComponents/instrumentForm'
import CreateOrderSideForm, { sideSchema } from './formComponents/sideForm'
import CreateOrderTargetStrategyForm, { targetStrategySchema } from './formComponents/strategyForm'
import CreateOrderUnitPriceForm, { unitPriceSchema } from './formComponents/unitPriceForm'

interface Props {
    clientCode: string
    marketCode: string
    onSuccess: () => void
    onOpenChange?: (open: boolean) => void
    instrumentSymbol?: string
}

export default function PlaceOrderDialog({ clientCode, marketCode, onSuccess, onOpenChange, instrumentSymbol }: Props) {
    const { toast } = useToast()
    // We'll have to rethink how to pull in instruments if someone does get
    // over 500. Will probably have to be a component with a search on it
    const { data: instruments } = useGetAllClientMarketInstruments(clientCode, marketCode, {
        Limit: 500,
    })

    const formSchema = sideSchema
        .extend(instrumentSymbolSchema.shape)
        .extend(targetStrategySchema.shape)
        .extend(unitPriceSchema.shape)
        .extend(clientRefSchema.shape)

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        mode: 'onBlur',
        defaultValues: {
            side: 'Buy',
            targetStrategy: 'CentralLimit',
            instrumentSymbol: instrumentSymbol,
        },
    })

    const createOrderMutator = useCreateClientMarketOrder(clientCode, marketCode, form.getValues('clientRef'), {
        mutation: {
            onSuccess: data => {
                console.log('Success', data)
                onSuccess()
            },
            onError: error => {
                console.error('Error creating order', error)
                toast({
                    variant: 'destructive',
                    title: 'Order placement failed!',
                    description: 'Order placement failed, please try again',
                })
            },
        },
    })

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        createOrderMutator.mutate({
            symbol: values.instrumentSymbol,
            side: values.side,
            targetStrategy: values.targetStrategy,
            price: values.price.toString(),
            units: values.units.toString(),
        })
    }

    return (
        <Dialog onOpenChange={onOpenChange} open={true}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Place Order</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <CreateOrderClientRefForm form={form as any} />
                        <CreateOrderSideForm form={form as any} />
                        <CreateOrderInstrumentSymbolForm
                            form={form as any}
                            instrumentData={instruments?.data || undefined}
                            disabled={!!instrumentSymbol}
                        />
                        <CreateOrderUnitPriceForm form={form as any} />
                        <CreateOrderTargetStrategyForm form={form as any} />

                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="outline" disabled={createOrderMutator.isPending}>
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button type="submit" disabled={!form.formState.isValid || createOrderMutator.isPending}>
                                Place order
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
